import React from 'react';
import clsx from 'clsx';
import { AnimatePresence, motion } from 'motion/react';
import css from './drop-overlay.module.scss';
import Typography from '../Typography/Typography';
import IconDefault from '../Icon/Icon';
import Spacer from '../Spacer/Spacer';
import { useTranslation } from 'react-i18next';

const contentEase = [0.22, 1, 0.36, 1] as const;
const exitEase = [0.4, 0, 1, 1] as const;

type Props = {
  active: boolean;
  icon?: React.ComponentType<{ className: string }>;
};

const DropOverlay = ({
  active,
  children,
  icon: Icon,
}: React.PropsWithChildren<Props>) => {
  const { t } = useTranslation();

  return (
    <AnimatePresence>
      {active && (
        <motion.div
          key="drop-overlay"
          className={clsx(css.root)}
          initial={{ opacity: 0 }}
          animate={{ opacity: 0.7 }}
          exit={{
            opacity: 0,
            transition: { duration: 0.22, ease: exitEase },
          }}
          transition={{ duration: 0.25, ease: 'easeOut' }}
        >
          <div className={css.content}>
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{
                opacity: 0,
                y: 10,
                transition: { duration: 0.2, delay: 0.04, ease: exitEase },
              }}
              transition={{
                duration: 0.4,
                delay: 0.06,
                ease: contentEase,
              }}
            >
              {Icon ? (
                <Icon className={css.icon} />
              ) : (
                <IconDefault
                  name="fa-sharp fa-regular fa-cloud-arrow-up"
                  className={css.icon}
                />
              )}
            </motion.div>
            <Spacer />
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{
                opacity: 0,
                y: 10,
                transition: { duration: 0.2, ease: exitEase },
              }}
              transition={{
                duration: 0.4,
                delay: 0.14,
                ease: contentEase,
              }}
            >
              <Typography color="inherit">
                {children || t('dropzone_drag_and_drop_files')}
              </Typography>
            </motion.div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default DropOverlay;
